import { Card, Col, Form, Row, Select } from 'antd';
import { NamePath } from 'antd/lib/form/interface';
import { DefaultOptionType } from 'antd/lib/select';
import { useGeneralFormDependencies } from 'client/modules/cbi-entity/services/general-info/hooks/useGeneralFormDependencies';
import { useSearchForCity } from 'client/modules/cbi-entity/services/general-info/hooks/useSearchForCity';
import { useCallback, useState } from 'react';
import { debounce } from 'lodash';
import { useAllStateValues } from '../services/hooks';
import { DataFeedFirmographicsFilters } from './DataFeedForm';

const continents = [
  { value: 1, label: 'Africa' },
  { value: 2, label: 'Antarctica' },
  { value: 3, label: 'Asia' },
  { value: 4, label: 'Australia' },
  { value: 5, label: 'Europe' },
  { value: 6, label: 'North America' },
  { value: 7, label: 'South America' },
];

const regions = [
  { value: 1, label: 'Silicon Valley' },
  { value: 2, label: 'DC / VA Metro' },
  { value: 3, label: 'Massachusetts / New England' },
  { value: 4, label: 'Midwest' },
  { value: 5, label: 'New York Metro' },
  { value: 6, label: 'Pacific Northwest' },
  { value: 7, label: 'Philadelphia Metro' },
  { value: 9, label: 'So Cal (LA/SD)' },
  { value: 10, label: 'SouthEast' },
  { value: 11, label: 'SouthWest' },
  { value: 12, label: 'Eastern Europe' },
  { value: 13, label: 'Southeastern Europe' },
  { value: 14, label: 'Balkans' },
  { value: 15, label: 'North Africa' },
  { value: 16, label: 'MENA' },
  { value: 17, label: 'Oceania' },
  { value: 18, label: 'Polynesia' },
  { value: 19, label: 'Caribbean' },
  { value: 20, label: 'Latin America' },
  { value: 21, label: 'Caucasus' },
  { value: 22, label: 'APAC (Asia-Pacific)' },
  { value: 23, label: 'Australasia' },
  { value: 24, label: 'Middle East' },
  { value: 25, label: 'South Asia' },
  { value: 26, label: 'Central America' },
  { value: 27, label: 'North America' },
  { value: 28, label: 'Southeast Asia' },
  { value: 29, label: 'East Asia' },
  { value: 30, label: 'Central Europe' },
  { value: 31, label: 'Nordics' },
  { value: 32, label: 'Scandinavia' },
  { value: 33, label: 'Baltics' },
  { value: 34, label: 'South America' },
  { value: 35, label: 'Melanesia' },
  { value: 36, label: 'Micronesia' },
  { value: 37, label: 'North Asia' },
];

export const ContinentSelect = ({
  name,
  label,
  placeholder,
}: {
  name: NamePath;
  label: string;
  placeholder: string;
}) => {
  return (
    <Form.Item name={name} label={label}>
      <Select
        className="min-w-[200px]"
        mode="multiple"
        placeholder={placeholder}
        allowClear
        filterOption={(input, option) =>
          !!option && option.label.toLowerCase().includes(input.toLowerCase())
        }
        options={continents}
        labelInValue
      />
    </Form.Item>
  );
};

export const RegionSelect = ({
  name,
  label,
  placeholder,
}: {
  name: NamePath;
  label: string;
  placeholder: string;
}) => {
  return (
    <Form.Item name={name} label={label}>
      <Select
        className="min-w-[200px]"
        mode="multiple"
        placeholder={placeholder}
        allowClear
        filterOption={(input, option) =>
          !!option && option.label.toLowerCase().includes(input.toLowerCase())
        }
        options={regions}
        labelInValue
      />
    </Form.Item>
  );
};
export const StateSelect = ({
  idCountry,
  label,
  names,
}: {
  idCountry: number;
  label: string;
  names: string[];
}) => {
  const { data, isFetching } = useAllStateValues({ idCountry });

  return (
    <Form.Item
      name={[...names, DataFeedFirmographicsFilters.states, idCountry]}
      label={label}
      className="mb-0"
      preserve={false}
    >
      <Select
        className="min-w-[200px]"
        mode="multiple"
        placeholder="Select States"
        allowClear
        filterOption={(input, option) =>
          !!option && option.label.toLowerCase().includes(input.toLowerCase())
        }
        labelInValue
        options={data?.stateInfo?.map((state) => ({
          value: state.idState,
          label: state.name,
        }))}
        loading={isFetching}
      />
    </Form.Item>
  );
};

export const CitySelect = ({
  idState,
  idCountry,
  label,
  names,
}: {
  idState: number;
  idCountry: number;
  label: string;
  names: string[];
}) => {
  const [search, setSearch] = useState<string>('');
  const { data, isFetching } = useSearchForCity(
    {
      idState,
      idCountry,
      name: search,
    },
    true
  );

  const debounceFetcher = useCallback(
    debounce((searchTerm) => {
      setSearch(searchTerm);
    }, 300),
    []
  );

  return (
    <Form.Item
      name={[...names, DataFeedFirmographicsFilters.cities, idCountry, idState]}
      label={label}
      className="mb-0"
      preserve={false}
    >
      <Select
        className="min-w-[200px]"
        mode="multiple"
        placeholder="Select Cities"
        allowClear
        showArrow={false}
        filterOption={false}
        showSearch
        onSearch={debounceFetcher}
        labelInValue
        options={data?.map((city) => ({
          value: city.idCity,
          label: city.name,
        }))}
        loading={isFetching}
      />
    </Form.Item>
  );
};

export const CountryStateCitySelector = ({ names }: { names: string[] }) => {
  const { data, isFetching } = useGeneralFormDependencies({
    enabled: true,
  });
  const allCountryValues = data?.allCountryValues;

  return (
    <>
      <Form.Item
        name={[...names, DataFeedFirmographicsFilters.countries]}
        label="Countries"
        className="mb-0"
      >
        <Select
          className="min-w-[200px]"
          mode="multiple"
          placeholder="Select countries"
          allowClear
          filterOption={(input, option) =>
            !!option && option.label.toLowerCase().includes(input.toLowerCase())
          }
          labelInValue
          options={allCountryValues?.countryInfo?.map((country) => ({
            value: country.idCountry,
            label: country.name,
          }))}
          loading={isFetching}
        />
      </Form.Item>
      <Form.Item shouldUpdate className="mb-0">
        {({ getFieldValue }) => {
          const countries: DefaultOptionType[] = getFieldValue([
            ...names,
            DataFeedFirmographicsFilters.countries,
          ]);
          return countries?.map((country) => {
            const countryStates = getFieldValue([
              ...names,
              DataFeedFirmographicsFilters.states,
              Number(country.value),
            ]);
            return (
              <Card title={country.label} bordered={false} key={country.value}>
                <Row gutter={[16, 16]}>
                  <Col span={24}>
                    <StateSelect
                      idCountry={Number(country.value)}
                      label="States"
                      names={names}
                    />
                  </Col>

                  {countryStates?.map((state: DefaultOptionType) => (
                    <Col span={6}>
                      <CitySelect
                        key={state.value}
                        idCountry={Number(country.value)}
                        idState={Number(state.value)}
                        label={`${state.label}'s Cities`}
                        names={names}
                      />
                    </Col>
                  ))}
                </Row>
              </Card>
            );
          });
        }}
      </Form.Item>
    </>
  );
};
